import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { BaseService } from './route.service';

/// O que a IA achou na conversa. Nulo é "não achou" ou "já está preenchido".
export interface AiLeadInsights {
    value: number | null;
    expectedCloseDate: string | null;
    email: string | null;
    cnpj: string | null;
    companyName: string | null;
    /// Hora local, sem fuso: é a hora que o cliente escreveu.
    nextStep: { scheduledAt: string; description: string } | null;
}

export interface AiLossSuggestion {
    lossReasonId: string | null;
    note: string | null;
}

/// A hora do relógio de quem usa, sem fuso; a IA converte "sexta às 14h" a partir dela.
const localNow = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
        + `T${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
};

export interface AiStatus {
    configured: boolean;
    /// Há modelo de áudio no servidor para transcrever.
    canTranscribe: boolean;
}

/// O id de conversa se repete entre caixas: a caixa vai junto para achar o lead certo.
const withInbox = (url: string, inboxId: number | null | undefined) =>
    inboxId === null || inboxId === undefined ? url : `${url}?inboxId=${inboxId}`;

export class AiService extends BaseService {
    protected get resourceName(): string {
        return 'crm/ai';
    }

    public async status(): Promise<AxiosResponse<{ data: AiStatus }>> {
        return await this.get('status');
    }

    public async transcribe(
        conversationId: number,
        inboxId: number | null | undefined,
        messageId: number
    ): Promise<AxiosResponse<{ data: { text: string } }>> {
        return await this.post({}, withInbox(`conversations/${conversationId}/messages/${messageId}/transcribe`, inboxId));
    }

    public async summarize(
        conversationId: number,
        inboxId: number | null | undefined
    ): Promise<AxiosResponse<{ data: { text: string } }>> {
        return await this.post({}, withInbox(`conversations/${conversationId}/summary`, inboxId));
    }

    public async insights(leadId: string): Promise<AxiosResponse<{ data: AiLeadInsights }>> {
        return await this.post({ now: localNow() }, `leads/${leadId}/insights`);
    }

    public async lossReason(leadId: string): Promise<AxiosResponse<{ data: AiLossSuggestion }>> {
        return await this.post({}, `leads/${leadId}/loss-reason`);
    }

    public async suggestReply(
        conversationId: number,
        inboxId: number | null | undefined
    ): Promise<AxiosResponse<{ data: { text: string } }>> {
        return await this.post({}, withInbox(`conversations/${conversationId}/suggest-reply`, inboxId));
    }
}

/// Uma pergunta por carga de página: a configuração só muda quando o servidor reinicia.
let statusRequest: Promise<AiStatus> | null = null;

const loadStatus = () => {
    statusRequest ??= new AiService()
        .status()
        .then((res) => ({
            configured: Boolean(res.data.data?.configured),
            canTranscribe: Boolean(res.data.data?.canTranscribe),
        }))
        .catch(() => {
            // Falhar não fica guardado: a próxima tela pergunta de novo.
            statusRequest = null;
            return { configured: false, canTranscribe: false };
        });

    return statusRequest;
};

/// O que a IA do servidor faz; nulo enquanto pergunta, para não piscar um botão desligado.
export const useAi = () => {
    const [status, setStatus] = useState<AiStatus | null>(null);

    useEffect(() => {
        let active = true;
        loadStatus().then((loaded) => { if (active) setStatus(loaded); });
        return () => { active = false; };
    }, []);

    return status;
};

/// Se a IA está ligada. Nulo enquanto pergunta.
export const useAiStatus = () => useAi()?.configured ?? null;
